'use client';

/**
 * Dialog "Log In" otorisasi user lain (supervisor) — padanan Delphi TFormMGSYLogIn
 * (Forms/System/MGSYLoginForm.pas + .dfm) yang dipanggil mis. TFormMGARTRJual.Password
 * (MGARTRJualForm.pas:5151-5177) saat user aktif tidak punya hak.
 *
 * - Judul "<Nama Perusahaan> - Log In" (Delphi FormShow: Company.Name + ' - ' + Caption; web: NmComp
 *   Global Information, dimuat sekali per sesi), "Log In" bila nama perusahaan kosong / gagal dimuat.
 * - "User Name :" (maks 20) & "Password :" (maks 80, tersamar); OK (Enter) / Cancel (Escape).
 * - Tidak pernah diisi otomatis (Delphi v.3 mengisi 'admin'/'admin' — sisa debug, plan §12 #20).
 * - OK, urutan Delphi ButtonOkClick: panjang User Name < 3 → 'Panjang user name minimal 3 karakter.';
 *   panjang Password < 3 → 'Panjang password minimal 3 karakter.' (selalu gagal apa pun isi DB, jadi
 *   dicek tanpa memanggil BE); lalu `verify({ kdmuser, pwd })`:
 *     · ok → `onSuccess(hasil)`;
 *     · status 403 (user valid tetapi tidak punya hak) → dialog ditutup lewat `onClose`; pemanggil
 *       menampilkan pesan tidak berhak (Delphi: Log In tertutup, lalu wmUserNoAuth);
 *     · gagal lain (400) → Swal "Peringatan" berisi pesan BE ('User name atau password salah.' /
 *       'User tidak aktif.'), dialog tetap terbuka, fokus ke User Name (Delphi);
 *     · error jaringan / 5xx → Swal "Gagal", dialog tetap terbuka.
 * - Tidak mengganti sesi user yang sedang login. Endpoint otorisasi wajib membalas 400/403 (bukan
 *   401) — interceptor axios me-logout paksa setiap respons 401.
 * - Dirender lewat portal ke <body>, tanpa <form>: event submit dari portal ikut merambat ke form
 *   transaksi di pohon React, jadi Enter ditangani manual.
 * - Fokus dijaga di dalam dialog: Swal halaman yang baru tertutup (mis. konfirmasi "... Apakah ingin
 *   diterima") mengembalikan fokus ke elemen lamanya ±100 ms kemudian — tanpa penjaga, ketikan
 *   (termasuk password) masuk ke kotak SN di belakang dialog. Escape milik Swal tidak menutup dialog.
 *
 * Pola pakai dengan promise (mis. di dalam alur async dialog SN):
 *   const otor = useOtorisasi((cred) => otorisasiHasil({ ...cred, menu: 'Retur Penjualan', flag: 'CanChangeGdPOS' }));
 *   const hasil = await otor.minta();   // null = Cancel / Escape / tidak punya hak (403)
 *   ...
 *   return <>{...}{otor.dialog}</>;
 */

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { get as getGlobalInfo } from '@/services/globalInfo.service';
import useAuthStore from '@/stores/authStore';

export interface OtorisasiKredensial {
    kdmuser: string;
    pwd: string;
}

export interface OtorisasiHasil {
    ok: boolean;
    /** Status HTTP respons otorisasi — 403 = user valid tetapi tidak punya hak (dialog ditutup) */
    status?: number;
    /** Pesan gagal (Delphi: "User name atau password salah.", "User tidak aktif.", ...) */
    message?: string;
    /** Token otorisasi singkat dari BE (ditempel ke data yang diotorisasi, dicek ulang BE saat simpan) */
    token?: string;
}

interface OtorisasiModalProps {
    open: boolean;
    /** Cancel / Escape / tombol X / tidak punya hak (403) */
    onClose: () => void;
    verify: (cred: OtorisasiKredensial) => Promise<OtorisasiHasil>;
    onSuccess: (hasil: OtorisasiHasil, cred: { kdmuser: string }) => void;
    /** Caption dialog (bawaan "Log In"); judul = "<Nama Perusahaan> - <title>" bila nama perusahaan ada */
    title?: string;
}

/** Pesan Log In Delphi (Units/MGMessage.pas:28-30, dipakai TFormMGSYLogIn.ButtonOkClick) */
export const pesanLogIn = {
    /** Format(wmLoginFieldLength, [field, '3']) — 'Panjang %s minimal %s karakter.' (MGMessage.pas:28) */
    panjangMinimal: (field: 'user name' | 'password') => `Panjang ${field} minimal 3 karakter.`,
    /** wmLoginNotActive (MGMessage.pas:29) */
    tidakAktif: 'User tidak aktif.',
    /** wmLoginFailed (MGMessage.pas:30) */
    gagal: 'User name atau password salah.',
};

const inputCls = 'h-9 w-full px-2.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50';

/** Pesan error dari axios / BE */
function pesanError(err: unknown): string {
    const e = err as { response?: { data?: { message?: unknown; data?: { message?: unknown } } }; message?: unknown };
    const m = e?.response?.data?.data?.message ?? e?.response?.data?.message ?? e?.message;
    return typeof m === 'string' && m.trim() ? m : 'Gagal';
}

/**
 * Modal bawaan me-reset `overflow` body saat ditutup. Bila dialog ini ditumpuk di atas dialog lain
 * (mis. dialog SN), kunci scroll body lagi selama masih ada dialog (bukan Swal) yang terbuka.
 */
function kunciScrollBilaMasihAdaDialog() {
    if (typeof document === 'undefined') return;
    if (document.querySelector('[role="dialog"][aria-modal="true"]:not(.swal2-popup)')) {
        document.body.style.overflow = 'hidden';
    }
}

// ─── Nama perusahaan (judul) ────────────────────────────────────────────────

/** Global Information NmComp — null = belum dimuat; '' = kosong */
let namaPerusahaanCache: string | null = null;
let namaPerusahaanMuat: Promise<string> | null = null;

/** Muat NmComp sekali per sesi (gagal → '' tanpa cache, dicoba lagi saat dialog dibuka berikutnya). */
function muatNamaPerusahaan(): Promise<string> {
    if (namaPerusahaanCache !== null) return Promise.resolve(namaPerusahaanCache);
    if (!namaPerusahaanMuat) {
        namaPerusahaanMuat = getGlobalInfo()
            .then((res) => {
                const nm = (res as { data?: { data?: { NmComp?: unknown } } } | undefined)?.data?.data?.NmComp;
                namaPerusahaanCache = typeof nm === 'string' ? nm.trim() : '';
                return namaPerusahaanCache;
            })
            .catch(() => '')
            .finally(() => { namaPerusahaanMuat = null; });
    }
    return namaPerusahaanMuat;
}

/** Fokus + pilih isi (Delphi TEdit.AutoSelect saat SetFocus). */
function fokus(ref: RefObject<HTMLInputElement | null>) {
    window.requestAnimationFrame(() => {
        ref.current?.focus();
        ref.current?.select();
    });
}

/** Swal tanpa returnFocus: fokus diatur dialog ini sendiri setelah Swal tertutup. */
async function pesan(title: string, text: string, icon: 'warning' | 'error') {
    await Swal.fire({ title, text, icon, returnFocus: false });
}

/** Dialog di-mount ulang setiap dibuka → field selalu kosong (tidak pernah prefill). */
export default function OtorisasiModal(props: OtorisasiModalProps) {
    if (!props.open || typeof document === 'undefined') return null;
    return createPortal(<OtorisasiDialog {...props} />, document.body);
}

function OtorisasiDialog({ open, onClose, verify, onSuccess, title = 'Log In' }: OtorisasiModalProps) {
    const [kdmuser, setKdmuser] = useState('');
    const [pwd, setPwd] = useState('');
    const [busy, setBusy] = useState(false);
    const [namaPerusahaan, setNamaPerusahaan] = useState<string>(() => namaPerusahaanCache ?? '');
    const idUser = useId();
    const idPwd = useId();
    const isiRef = useRef<HTMLDivElement>(null);
    const userRef = useRef<HTMLInputElement>(null);
    const pwdRef = useRef<HTMLInputElement>(null);
    /** Elemen terakhir yang fokus di dalam dialog (dipulihkan bila fokus dicuri ke luar) */
    const terakhirRef = useRef<HTMLElement | null>(null);
    const aliveRef = useRef(true);
    const busyRef = useRef(false);
    /** Escape terakhir milik Swal / dialog lain → jangan Cancel dialog ini */
    const escLewatiRef = useRef(false);
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            // setelah cleanup Modal (yang me-reset overflow body) selesai
            window.setTimeout(kunciScrollBilaMasihAdaDialog, 0);
        };
    }, []);

    useEffect(() => {
        let hidup = true;
        void muatNamaPerusahaan().then((nm) => { if (hidup) setNamaPerusahaan(nm); });
        return () => { hidup = false; };
    }, []);

    // Escape (fase capture, sebelum handler Modal): catat bila milik Swal / dialog lain.
    // Tab berputar di dalam dialog seperti TabOrder Delphi MGSYLoginForm: Tab di elemen terakhir (OK) → User Name,
    // Shift+Tab di elemen pertama (X) → OK. Fokus yang tetap lolos ke luar dialog (bukan ke Swal) → dari elemen
    // terakhir ke User Name, dari elemen pertama ke elemen terakhir; selain itu (mis. pemulihan fokus Swal
    // halaman) → kembali ke field terakhir / User Name.
    useEffect(() => {
        const milikKita = () => isiRef.current?.closest('[role="dialog"]') ?? null;
        const bisaTab = (dlg: Element) => Array.from(dlg.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )).filter((el) => el.tabIndex >= 0 && !(el as HTMLInputElement).disabled && el.getClientRects().length > 0);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                const dlg = milikKita();
                const aktif = document.activeElement;
                if (!dlg || !(aktif instanceof HTMLElement) || !dlg.contains(aktif) || Swal.isVisible()) return;
                const daftar = bisaTab(dlg);
                if (daftar.length < 2) return;
                const tujuan = !e.shiftKey && aktif === daftar[daftar.length - 1] ? userRef.current
                    : (e.shiftKey && aktif === daftar[0] ? daftar[daftar.length - 1] : null);
                if (tujuan) {
                    e.preventDefault();
                    tujuan.focus();
                }
                return;
            }
            if (e.key !== 'Escape') return;
            const target = e.target instanceof Element ? e.target : null;
            const dialogTarget = target?.closest('[role="dialog"]') ?? null;
            escLewatiRef.current = Swal.isVisible() || (dialogTarget !== null && dialogTarget !== milikKita());
            window.setTimeout(() => { escLewatiRef.current = false; }, 0);
        };
        const onFocusIn = (e: FocusEvent) => {
            const dlg = milikKita();
            const target = e.target instanceof HTMLElement ? e.target : null;
            if (!dlg || !target) return;
            if (dlg.contains(target)) {
                terakhirRef.current = target;
                return;
            }
            if (target.closest('.swal2-container')) return;
            const daftar = bisaTab(dlg);
            const akhir = terakhirRef.current;
            let tujuan: HTMLElement | null;
            if (akhir && daftar.length > 1 && akhir === daftar[daftar.length - 1]) tujuan = userRef.current;
            else if (akhir && daftar.length > 1 && akhir === daftar[0]) tujuan = daftar[daftar.length - 1];
            else tujuan = akhir && dlg.contains(akhir) && !(akhir as HTMLInputElement).disabled ? akhir : userRef.current;
            tujuan?.focus();
        };
        window.addEventListener('keydown', onKey, true);
        document.addEventListener('focusin', onFocusIn);
        return () => {
            window.removeEventListener('keydown', onKey, true);
            document.removeEventListener('focusin', onFocusIn);
        };
    }, []);

    /** Escape / tombol X (onClose Modal) = Cancel, kecuali Escape itu menutup Swal di atas dialog ini. */
    const tutup = useCallback(() => {
        const lewati = escLewatiRef.current;
        escLewatiRef.current = false;
        if (lewati || Swal.isVisible()) return;
        onCloseRef.current();
    }, []);

    const handleOk = async () => {
        if (busyRef.current) return;
        // Delphi ButtonOkClick: panjang < 3 selalu gagal dengan pesan panjang (didahulukan dari pesan lain)
        if (kdmuser.length < 3) {
            await pesan('Peringatan', pesanLogIn.panjangMinimal('user name'), 'warning');
            if (aliveRef.current) fokus(userRef);
            return;
        }
        if (pwd.length < 3) {
            await pesan('Peringatan', pesanLogIn.panjangMinimal('password'), 'warning');
            if (aliveRef.current) fokus(pwdRef);
            return;
        }
        busyRef.current = true;
        setBusy(true);
        let hasil: OtorisasiHasil | null = null;
        let galat: { err: unknown } | null = null;
        try {
            hasil = await verify({ kdmuser, pwd });
        } catch (err) {
            galat = { err };
        } finally {
            busyRef.current = false;
            if (aliveRef.current) setBusy(false);
        }
        if (!aliveRef.current) return;
        if (galat) {
            await pesan('Gagal', pesanError(galat.err), 'error');
            if (aliveRef.current) fokus(userRef);
            return;
        }
        if (hasil?.ok) {
            onSuccess(hasil, { kdmuser });
            return;
        }
        if (hasil?.status === 403) {
            // user valid tetapi tidak punya hak → Log In ditutup; pemanggil menampilkan pesan tidak berhak
            onCloseRef.current();
            return;
        }
        const teks = typeof hasil?.message === 'string' && hasil.message.trim() ? hasil.message : pesanLogIn.gagal;
        await pesan('Peringatan', teks, 'warning');
        if (aliveRef.current) fokus(teks === pesanLogIn.panjangMinimal('password') ? pwdRef : userRef);
    };

    const onEnter = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent.isComposing) return;
        void handleOk();
    };

    return (
        <Modal
            open={open}
            onClose={tutup}
            title={namaPerusahaan ? `${namaPerusahaan} - ${title}` : title}
            size="sm"
            closeOnBackdrop={false}
            footer={
                <>
                    <Button variant="secondary" onClick={() => onCloseRef.current()}>Cancel</Button>
                    <Button onClick={() => void handleOk()} loading={busy}>OK</Button>
                </>
            }
        >
            <div ref={isiRef} className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-3">
                <label htmlFor={idUser} className="text-sm text-slate-700 text-right whitespace-nowrap">User Name :</label>
                <input
                    ref={userRef}
                    id={idUser}
                    type="text"
                    name="otorisasi-user-name"
                    value={kdmuser}
                    maxLength={20}
                    autoFocus
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    readOnly={busy}
                    onChange={(e) => setKdmuser(e.target.value)}
                    onKeyDown={onEnter}
                    className={inputCls}
                />
                <label htmlFor={idPwd} className="text-sm text-slate-700 text-right whitespace-nowrap">Password :</label>
                <input
                    ref={pwdRef}
                    id={idPwd}
                    type="password"
                    name="otorisasi-password"
                    value={pwd}
                    maxLength={80}
                    autoComplete="new-password"
                    readOnly={busy}
                    onChange={(e) => setPwd(e.target.value)}
                    onKeyDown={onEnter}
                    className={inputCls}
                />
            </div>
        </Modal>
    );
}

/**
 * Hook promise untuk dialog Log In: `minta()` membuka dialog dan resolve dengan hasil `verify`
 * yang sukses, atau `null` bila Cancel / Escape / tidak punya hak (403). Render `dialog` di halaman
 * pemanggil. Nama perusahaan untuk judul dimuat saat hook dipasang (judul siap saat dialog dibuka).
 */
export function useOtorisasi(
    verify: (cred: OtorisasiKredensial) => Promise<OtorisasiHasil>,
    title?: string,
): { minta: () => Promise<OtorisasiHasil | null>; dialog: ReactNode; open: boolean } {
    const [open, setOpen] = useState(false);
    const resolverRef = useRef<((hasil: OtorisasiHasil | null) => void) | null>(null);

    // Prefetch nama perusahaan hanya bila sudah login (pratinjau landing publik memakai hook ini tanpa sesi → 401);
    // dialog sendiri tetap memuatnya saat dibuka.
    useEffect(() => { if (useAuthStore.getState().accessToken) void muatNamaPerusahaan(); }, []);

    const selesai = useCallback((hasil: OtorisasiHasil | null) => {
        setOpen(false);
        const resolve = resolverRef.current;
        resolverRef.current = null;
        resolve?.(hasil);
    }, []);

    const minta = useCallback(() => new Promise<OtorisasiHasil | null>((resolve) => {
        resolverRef.current?.(null); // permintaan sebelumnya dianggap batal
        resolverRef.current = resolve;
        setOpen(true);
    }), []);

    // Unmount saat dialog masih terbuka → anggap batal (promise tidak menggantung)
    useEffect(() => () => {
        const resolve = resolverRef.current;
        resolverRef.current = null;
        resolve?.(null);
    }, []);

    const dialog = (
        <OtorisasiModal
            open={open}
            title={title}
            verify={verify}
            onClose={() => selesai(null)}
            onSuccess={(hasil) => selesai(hasil)}
        />
    );
    return { minta, dialog, open };
}
